/**
 * Prism brand theme — based on 2SD Technologies palette.
 *
 * Brand colors pulled from https://2sdtechnologies.com:
 *   - Brand Blue:    #5b92e5  (primary action)
 *   - Brand Orange:  #d8a878  (warm accent)
 *   - Accent Teal:   #7db9b3  (secondary)
 *   - Accent Beige:  #e8d5b5  (muted highlight)
 *
 * Apple-influenced design tokens:
 *   - Translucent surfaces (`rgba(255,255,255,.85)` light, `#141414` dark)
 *     with a `backdropFilter: blur(20px) saturate(140%)` effect
 *   - Tighter tracking, larger radii, softer shadows
 *
 * Use `prismLightTheme` / `prismDarkTheme` with `<FluentProvider theme={...}>`,
 * and `glass.surface` / `glass.surfaceStrong` as inline backgrounds where you
 * want the iOS-style frosted look.
 */
import {
  createLightTheme,
  createDarkTheme,
  type BrandVariants,
  type Theme,
} from '@fluentui/react-components';

// Brand ramp generated from #5b92e5 (10–160). Hand-tuned darker stops to keep
// brand foreground readable on light surfaces, lighter stops to stay distinct
// on dark.
const prismBrand: BrandVariants = {
  10: '#020409',
  20: '#0F1A2E',
  30: '#152A4D',
  40: '#1B3A6B',
  50: '#214B8A',
  60: '#285CA9',
  70: '#2F6EC8',
  80: '#3D80D6',
  90: '#5B92E5', // primary
  100: '#74A4EA',
  110: '#8DB6EE',
  120: '#A5C7F2',
  130: '#BED9F7',
  140: '#D6EAFB',
  150: '#E8F2FD',
  160: '#F4F8FE',
};

export const prismLightTheme: Theme = {
  ...createLightTheme(prismBrand),
  // Slightly cooler neutral than the default warm gray Fluent ships with.
  colorNeutralBackground1: '#FAFBFC',
  colorNeutralBackground2: '#F4F6F9',
  colorNeutralBackground3: '#EDEFF3',
  colorNeutralForeground1: '#1A1A1A',
  colorNeutralForeground2: '#3D3D3D',
  colorNeutralStroke1: 'rgba(0,0,0,0.08)',
  colorNeutralStroke2: 'rgba(0,0,0,0.06)',
  borderRadiusMedium: '8px',
  borderRadiusLarge: '14px',
  borderRadiusXLarge: '20px',
};

/**
 * Obsidian-influenced dark theme:
 *   - Very deep background (#1e1e1e), not navy
 *   - Off-white text (#dcddde), not pure white
 *   - Borders 1px and very faint, not shadow-based
 *   - Smaller radii — 6/10/14 instead of 8/14/20 — feels less "card-y"
 *   - Editorial mono font for IDs, counts, timestamps via `mono` token
 */
export const prismDarkTheme: Theme = {
  ...createDarkTheme(prismBrand),
  // Layered dark surfaces — Obsidian uses ~3 levels
  colorNeutralBackground1: '#1e1e1e',
  colorNeutralBackground2: '#262626',
  colorNeutralBackground3: '#2f2f2f',
  colorNeutralBackground1Hover: '#2a2a2a',
  // Editorial off-white, not stark white
  colorNeutralForeground1: '#dcddde',
  colorNeutralForeground2: '#a0a0a3',
  colorNeutralForeground3: '#7b7c80',
  // Borders, not shadows
  colorNeutralStroke1: '#3a3a3a',
  colorNeutralStroke2: '#2c2c2c',
  colorNeutralStroke3: '#262626',
  // Tighter, less rounded
  borderRadiusMedium: '6px',
  borderRadiusLarge: '10px',
  borderRadiusXLarge: '14px',
};

/**
 * Mono font stack for IDs, counts, timestamps — gives technical info
 * an editorial, "this-is-data" feel borrowed from Obsidian's status bar
 * and code blocks.
 */
export const mono = {
  fontFamily:
    '"SF Mono", ui-monospace, "JetBrains Mono", "Cascadia Code", "Roboto Mono", Menlo, Consolas, monospace',
  // Optical adjustment — mono fonts run a touch wider than sans, so trim
  fontFeatureSettings: '"tnum", "ss01"',
  letterSpacing: '-0.2px',
} as const;

/** Brand color shorthand for inline use (gradients, brand chips, etc). */
export const brand = {
  blue: '#5b92e5',
  blueDeep: '#3D80D6',
  orange: '#d8a878',
  teal: '#7db9b3',
  beige: '#e8d5b5',
  // Aurora gradient — used on the login surface and hero cards
  aurora:
    'radial-gradient(at 20% 0%, rgba(91,146,229,0.18) 0px, transparent 50%),' +
    'radial-gradient(at 80% 0%, rgba(125,185,179,0.14) 0px, transparent 50%),' +
    'radial-gradient(at 50% 100%, rgba(216,168,120,0.10) 0px, transparent 50%)',
} as const;

/**
 * Reusable glassmorphism surface presets.
 * Apply as inline `style={{ ...glass.surface }}` on a card / sheet wrapper.
 */
export const glass = {
  /** Default frosted card — subtle, suitable for inline content blocks. */
  surface: {
    background: 'rgba(255,255,255,0.66)',
    backdropFilter: 'blur(18px) saturate(140%)',
    WebkitBackdropFilter: 'blur(18px) saturate(140%)',
    border: '1px solid rgba(255,255,255,0.55)',
    boxShadow:
      '0 1px 0 rgba(255,255,255,0.6) inset, 0 8px 24px rgba(15,23,42,0.06)',
  } as const,
  /** Stronger frost — for floating sheets, modals, command palette. */
  surfaceStrong: {
    background: 'rgba(255,255,255,0.82)',
    backdropFilter: 'blur(28px) saturate(160%)',
    WebkitBackdropFilter: 'blur(28px) saturate(160%)',
    border: '1px solid rgba(255,255,255,0.7)',
    boxShadow:
      '0 1px 0 rgba(255,255,255,0.8) inset, 0 16px 40px rgba(15,23,42,0.10)',
  } as const,
  /** Sidebar / nav frost — slightly tinted with brand. */
  rail: {
    background:
      'linear-gradient(180deg, rgba(255,255,255,0.72), rgba(244,246,249,0.62))',
    backdropFilter: 'blur(20px) saturate(140%)',
    WebkitBackdropFilter: 'blur(20px) saturate(140%)',
    borderRight: '1px solid rgba(0,0,0,0.06)',
  } as const,
} as const;
